import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { userActions } from "../../../actions/user.actions";
import { OP_WORDS, OutcomeChip } from "./ruleDisplay";

const VERDICTS = ["green", "orange", "red"];

function Section({ title, children }) {
  return (
    <Box sx={{ marginBottom: "20px" }}>
      <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: "8px" }}>{title}</Typography>
      {children}
    </Box>
  );
}

function SmallTable({ head, children }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: "8px" }}>
      <TableContainer>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#F8F8F8" }}>
            <TableRow>
              {head.map((label) => (
                <TableCell key={label}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// Changes + sample-file check + impact on recent applicants, then publish (with override if a sample verdict changed)
export default function ReviewPublishDialog({ open, policyId, changes, onClose, onPublished }) {
  const dispatch = useDispatch();
  const [preview, setPreview] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);

  const patches = changes.map(({ row, newValue }) => ({
    nodeId: row.nodeId,
    ruleId: row.ruleId,
    inputId: row.inputId,
    newValue,
  }));

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setErrorMessage(null);
    dispatch(userActions.previewRinuxRuleImpact(policyId, patches))
      .then(setPreview)
      .catch((err) => setErrorMessage(err?.response?.data?.message || "Could not preview this change."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validation = preview?.validation;
  const blocking = validation?.blocking || [];
  const sampleChanges = (validation?.samples || []).filter((s) => s.expected && s.overall !== s.expected);
  const worseCount = (preview?.flips || []).filter((flip) => flip.worse).length;

  const handlePublish = () => {
    setIsPublishing(true);
    setErrorMessage(null);
    dispatch(userActions.saveRinuxRuleThresholds(policyId, patches, sampleChanges.length > 0))
      .then(() => onPublished(sampleChanges.length ? "Published with override" : "Published as a new version"))
      .catch((err) => {
        const data = err?.response?.data;
        setErrorMessage([data?.message || "Could not publish.", ...(data?.validation?.blocking || [])].join(" · "));
      })
      .finally(() => setIsPublishing(false));
  };

  return (
    <Dialog open={open} onClose={isPublishing ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Review &amp; Publish</DialogTitle>
      <DialogContent>
        {errorMessage && <Alert severity="error" sx={{ marginBottom: "16px" }}>{errorMessage}</Alert>}

        <Section title="Changes">
          <SmallTable head={["Check", "Condition", "Current", "New"]}>
            {changes.map(({ check, row, newValue }) => (
              <TableRow key={`${row.nodeId}/${row.ruleId}`}>
                <TableCell sx={{ fontWeight: 500 }}>{check.name}</TableCell>
                <TableCell>{OP_WORDS[row.op] || row.op}</TableCell>
                <TableCell sx={{ color: "text.secondary", textDecoration: "line-through" }}>{row.value}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{newValue}</TableCell>
              </TableRow>
            ))}
            {(preview?.textChanges || []).map((change) => (
              <TableRow key={`${change.check}-${change.ruleId}`}>
                <TableCell sx={{ fontWeight: 500 }}>{change.check}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>Reason text</TableCell>
                <TableCell sx={{ color: "text.secondary", textDecoration: "line-through" }}>{change.before}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{change.after}</TableCell>
              </TableRow>
            ))}
          </SmallTable>
        </Section>

        {!preview && !errorMessage && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px 0" }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Checking sample files and recent applicants…</Typography>
          </Box>
        )}

        {preview && (
          <>
            <Section title="Sample files">
              <SmallTable head={["Sample", "Expected", "Result", "Status"]}>
                {validation.samples.map((sample) => {
                  const changed = sample.expected && sample.overall !== sample.expected;
                  return (
                    <TableRow key={sample.name}>
                      <TableCell>{sample.name}</TableCell>
                      <TableCell>{sample.expected ? <OutcomeChip color={sample.expected} /> : "—"}</TableCell>
                      <TableCell><OutcomeChip color={sample.overall} /></TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="fibePill"
                          color={changed ? "warning" : "success"}
                          label={changed ? "Changed" : "Pass"}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {validation.samples.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: "text.secondary" }}>No sample files for this policy.</TableCell>
                  </TableRow>
                )}
              </SmallTable>
              <Box sx={{ marginTop: "12px" }}>
                {blocking.length > 0 && <Alert severity="error">This change can't be published: {blocking.join(" · ")}</Alert>}
                {!blocking.length && sampleChanges.length > 0 && (
                  <Alert severity="warning">
                    {sampleChanges.map((s) => `${s.name} (${s.expected} → ${s.overall})`).join(", ")} changed verdict. Publish
                    only if this is intended.
                  </Alert>
                )}
                {!blocking.length && !sampleChanges.length && (
                  <Alert severity="success">All sample files still get their expected result.</Alert>
                )}
              </Box>
            </Section>

            <Section title={`Impact on recent applicants (${preview.cohortSize})`}>
              {preview.cohortSize === 0 ? (
                <Alert severity="info">{preview.note}</Alert>
              ) : (
                <>
                  <SmallTable head={["Result", "Now", "After change", "Difference"]}>
                    {VERDICTS.map((verdict) => {
                      const difference = preview.after[verdict] - preview.before[verdict];
                      return (
                        <TableRow key={verdict}>
                          <TableCell><OutcomeChip color={verdict} /></TableCell>
                          <TableCell>{preview.before[verdict]}</TableCell>
                          <TableCell>{preview.after[verdict]}</TableCell>
                          <TableCell sx={{ fontWeight: difference ? 600 : 400 }}>
                            {difference > 0 ? `+${difference}` : difference}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </SmallTable>
                  <Typography
                    variant="body2"
                    sx={{ marginTop: "12px", color: worseCount ? "error.main" : "text.secondary" }}
                  >
                    {preview.flips.length
                      ? `${preview.flips.length} of ${preview.cohortSize} applicants change result, ${worseCount} to a worse one.`
                      : "No recent applicant changes result."}
                  </Typography>
                  {preview.flips.length > 0 && (
                    <Box sx={{ maxHeight: 220, overflowY: "auto", marginTop: "8px" }}>
                      <SmallTable head={["Application", "Now", "After change"]}>
                        {preview.flips.map((flip, index) => (
                          <TableRow key={`${flip.applicationId}-${index}`}>
                            <TableCell>{flip.applicationId || "—"}</TableCell>
                            <TableCell><OutcomeChip color={flip.from} /></TableCell>
                            <TableCell><OutcomeChip color={flip.to} /></TableCell>
                          </TableRow>
                        ))}
                      </SmallTable>
                    </Box>
                  )}
                </>
              )}
            </Section>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPublishing}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handlePublish}
          disabled={!preview || blocking.length > 0 || isPublishing}
          sx={{ color: "#fff" }}
        >
          {isPublishing ? "Publishing..." : sampleChanges.length ? "Override & Publish" : "Publish"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
